import React, { useState, useEffect } from 'react';
import { SelectableTile } from '@carvana/core-helios-forms';
import { Button } from '@carvana/core-helios-button';
import { datadogRum } from '@datadog/browser-rum';
import { Analytics } from '@services/analytics-service';
import { AnalyticEvents } from '@constants/index';
import { snapshot } from 'valtio';
import { SarStore } from '@store/sar-store';
import { SAR_DATA, Levels, heading as defaultHeading, SubCategory } from '../../constants';
import styles from '../../service-and-repair.module.css';
import { UserSelectionsType, useUserSelections } from '../../providers/user-selections';

type ParentData = {
  level: string;
  title?: string;
  text?: string;
  heading: {
    title?: string;
    text?: string;
  };
  categories?: {
    title: string;
    text: string;
  }[];
};

const getDataSubset = (userSelections, level) => {
  const { carvanaCareWarranty } = snapshot(SarStore);
  let dataSubset: any = SAR_DATA(carvanaCareWarranty);

  if (level !== Levels.Category) {
    dataSubset = dataSubset?.categories?.find(({ title }) => title === userSelections.category);
  }
  if (level === Levels.Subsubcategory || level === Levels.Location) {
    dataSubset = dataSubset?.categories?.find(({ title }) => title === userSelections.subcategory);
  }

  return dataSubset;
};

type SelectionListProps = {
  level: string;
  handleNextRoute: (dataSubset: any) => void;
};

const sendEvent = (
  level: string,
  handleType: 'selection' | 'next',
  title: string,
  userSelections: UserSelectionsType
) => {
  let eventName: string = '';
  let prop: Record<string, any> = {};

  switch (level) {
    case Levels.Category:
      if (handleType === 'selection') {
        eventName = AnalyticEvents.sarCategorySelectCategory;
        prop = { category: title };
      } else if (handleType === 'next') {
        eventName = AnalyticEvents.sarCategoryClickedNext;
        prop = { categorySelected: title };
      }

      break;
    case Levels.Subcategory:
      if (handleType === 'selection') {
        eventName = AnalyticEvents.sarSubCategorySelectSubCategory;
        prop = { subCategory: title };
      } else if (handleType === 'next') {
        eventName = AnalyticEvents.sarSubCategoryClickedNext;
        prop = { subCategorySelected: title };
      }

      break;
    case Levels.Subsubcategory:
      if (handleType === 'selection') {
        eventName = AnalyticEvents.sarSubSubCategorySelectSubSubCategory;
        prop = { subSubCategory: title };
      } else if (handleType === 'next') {
        eventName = AnalyticEvents.sarSubSubCategoryClickedNext;
        prop = { subSubCategorySelected: title };
      }

      break;
    case Levels.Location:
      if (handleType === 'selection') {
        eventName = AnalyticEvents.sarLocationSelectLocation;
        prop = { location: title };
      } else if (handleType === 'next') {
        eventName = AnalyticEvents.sarLocationClickedNext;
        prop = { locationSelected: title };
      }

      break;
    default:
      return '';
  }

  if (!eventName) {
    return;
  }

  Analytics.trackEvent(eventName, prop);
  datadogRum.addAction(eventName, handleType === 'selection' ? prop : { ...userSelections });
};

export const SelectionList = ({ level, handleNextRoute }: SelectionListProps) => {
  const { userSelections, updateUserSelections } = useUserSelections();
  const [parentData, setParentData] = useState<ParentData>({ level: '', heading: {} });

  const handleSelection = (title: string) => {
    updateUserSelections({ [level]: title });

    sendEvent(level, 'selection', title, userSelections);
  };

  const handleKeyPress = (event: React.KeyboardEvent, title: string) => {
    if (event.code === 'Space') {
      event.preventDefault();
      handleSelection(title);
    }
  };

  const handleNext = () => {
    const dataKey = level === Levels.Location ? 'locations' : 'categories';
    const selectionSubset = parentData?.[dataKey]?.find(({ title }) => title === userSelections[level]);
    if (!selectionSubset) {
      console.error('This should not happen');
    }
    const selection = selectionSubset || SAR_DATA;
    handleNextRoute(selection);

    sendEvent(level, 'next', (selection as any).title, userSelections);
  };

  useEffect(() => {
    const dataSubset = getDataSubset(userSelections, level);
    setParentData(dataSubset);
  }, []);

  const { heading = defaultHeading, categories = [], locations = [], title } = parentData as any;
  const { title: headingTitle, text: headingText } = heading;
  const isTireSubCategory = title === SubCategory.Tires;
  let headingTextFinal = headingText;
  if (isTireSubCategory) {
    headingTextFinal = (
      <>
        Examples include flat tires, blow outs, curb rash or debris-related damage. We <b>do not cover</b> normal wear
        or tear.
      </>
    );
  }

  const selections = level === Levels.Location ? locations : categories;

  return (
    <div className={styles['page-wrapper']}>
      <div className={styles['page-header']}>
        {headingTitle && <h2>{headingTitle}</h2>}
        {headingText && <p>{headingTextFinal}</p>}
      </div>
      <div className={styles['form-wrapper']}>
        {selections?.map(({ title, text }) => (
          <div tabIndex={0} key={title} onKeyDown={(e) => handleKeyPress(e, title)}>
            <SelectableTile
              key={title}
              id={`tile-${title}`}
              title={title}
              text={text}
              testId={`tile-${title}`}
              hideFormEl
              checked={userSelections[level] === title}
              onClick={() => handleSelection(title)}
              tabIndex={-1}
            />
          </div>
        ))}
      </div>

      <div className={styles['button-wrapper']}>
        <Button disabled={!userSelections[level]} onClick={handleNext}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default SelectionList;
